import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useBranding } from '../../../../hooks/useBranding';

const RelatedProducts = ({ products = [], loading = false }) => {
  const { primaryColor, secondaryColor } = useBranding();
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const colors = {
    primary: primaryColor || '#ff4d00',
    secondary: secondaryColor || '#fc7303'
  };

  const checkScroll = () => {
    if (!scrollRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
    setCanScrollLeft(scrollLeft > 10);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
  };

  const scroll = (direction) => {
    if (!scrollRef.current) return;
    const scrollAmount = 320;
    scrollRef.current.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth'
    });
    setTimeout(checkScroll, 350);
  };

  if (loading) {
    return (
      <div className="mt-16 mb-8">
        <div className="h-8 w-64 bg-gray-200 rounded-lg animate-pulse mb-8" />
        <div className="flex gap-5 overflow-hidden">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="flex-shrink-0 w-[260px]">
              <div className="aspect-[3/4] bg-gray-200 rounded-2xl animate-pulse" />
              <div className="mt-3 h-4 w-3/4 bg-gray-200 rounded animate-pulse" />
              <div className="mt-2 h-4 w-1/2 bg-gray-200 rounded animate-pulse" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!products || products.length === 0) return null;

  const placeholderSvg = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='400'%3E%3Crect width='300' height='400' fill='%23f3f4f6'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%239ca3af' font-size='14'%3ENo Image%3C/text%3E%3C/svg%3E";

  return (
    <div className="mt-16 mb-8 relative">
      {/* Section Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <motion.h2
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="text-2xl md:text-3xl font-bold text-gray-900"
          >
            Produits de la même catégorie
          </motion.h2>
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: '4rem' }}
            transition={{ delay: 0.3, duration: 0.5 }}
            className="h-1 rounded-full mt-3"
            style={{ backgroundColor: colors.primary }}
          />
        </div>

        {/* Scroll Controls */}
        {products.length > 4 && (
          <div className="hidden md:flex items-center gap-2">
            <button
              onClick={() => scroll('left')}
              disabled={!canScrollLeft}
              className={`p-2.5 rounded-full border-2 transition-all duration-300 ${
                canScrollLeft
                  ? 'border-gray-300 hover:border-gray-900 hover:bg-gray-900 hover:text-white text-gray-600'
                  : 'border-gray-200 text-gray-300 cursor-not-allowed'
              }`}
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={() => scroll('right')}
              disabled={!canScrollRight}
              className={`p-2.5 rounded-full border-2 transition-all duration-300 ${
                canScrollRight
                  ? 'border-gray-300 hover:border-gray-900 hover:bg-gray-900 hover:text-white text-gray-600'
                  : 'border-gray-200 text-gray-300 cursor-not-allowed'
              }`}
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        )}
      </div>

      {/* Products Carousel */}
      <div className="relative">
        {/* Fade edges */}
        {canScrollLeft && (
          <div className="absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none" />
        )}
        {canScrollRight && products.length > 4 && (
          <div className="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none" />
        )}

        <div
          ref={scrollRef}
          onScroll={checkScroll}
          className="flex gap-5 overflow-x-auto pb-4 scrollbar-hide snap-x snap-mandatory"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {products.map((product, index) => {
            const hasImage = product.imageUrl && product.imageUrl.length > 0 && product.imageUrl[0];
            const isOutOfStock = product.stock <= 0;

            return (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05, duration: 0.4 }}
                className="flex-shrink-0 w-[220px] sm:w-[240px] md:w-[260px] snap-start"
              >
                <Link
                  to={`/product/${product.id}/0`}
                  className="group block"
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                >
                  {/* Image Container */}
                  <div className="relative overflow-hidden rounded-2xl bg-gray-50 aspect-[3/4] mb-3">
                    <img
                      src={hasImage ? product.imageUrl[0] : placeholderSvg}
                      alt={product.name}
                      className="w-full h-full object-cover transition-all duration-700 group-hover:scale-110"
                      onError={(e) => { e.target.src = placeholderSvg; }}
                      loading="lazy"
                    />

                    {/* Hover overlay */}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all duration-500" />

                    {/* Quick view button */}
                    <div className="absolute inset-x-0 bottom-0 p-4 translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-out">
                      <div
                        className="w-full py-2.5 rounded-xl text-white text-sm font-medium text-center backdrop-blur-md"
                        style={{ backgroundColor: `${colors.primary}dd` }}
                      >
                        Voir le produit
                      </div>
                    </div>

                    {/* Promo badge */}
                    {product.enPromo && (
                      <div className="absolute top-3 left-3">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-bold text-white bg-red-500 shadow-lg">
                          PROMO
                        </span>
                      </div>
                    )}

                    {/* Out of stock badge */}
                    {isOutOfStock && (
                      <div className="absolute top-3 right-3">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium text-white bg-gray-800/80 backdrop-blur-sm">
                          Épuisé
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Product Info */}
                  <div className="px-1">
                    <h3 className="text-sm font-medium text-gray-900 group-hover:text-gray-600 transition-colors duration-300 line-clamp-2 leading-snug mb-1.5">
                      {product.name}
                    </h3>
                    <div className="flex items-center gap-2">
                      {product.enPromo && product.prixVentePromo > 0 ? (
                        <>
                          <span className="text-base font-bold" style={{ color: colors.primary }}>
                            {product.prixVentePromo.toFixed(2)} TND
                          </span>
                          <span className="text-sm text-gray-400 line-through">
                            {product.prixVenteTTC.toFixed(2)} TND
                          </span>
                        </>
                      ) : (
                        <span className="text-base font-bold text-gray-900">
                          {product.prixVenteTTC.toFixed(2)} TND
                        </span>
                      )}
                    </div>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>

      <style>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
};

export default RelatedProducts;
